'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Container, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionSpan = motion('span');

const ScrollAnimatedText = ({ text, progress, index }) => {
  const words = text ? text.split(' ') : [];
  const totalLetters = text ? text.replace(/\s+/g, '').length + 20 : 0;
  const visibleCount = Math.floor(progress * totalLetters);

  let letterIndex = 0;

  return (
    <Text
      as="div"
      display="flex"
      flexWrap="wrap"
      lineHeight={{ base: '33.6px', md: '57.6px', '2xl': '67.2px' }}
      fontSize={{ base: '28px', md: '48px', '2xl': '56px' }}
      fontWeight="400"
      color="white"
      fontFamily="'FedraSansStd-book', sans-serif"
      letterSpacing={{ base: '-0.56px', md: '0.96px', '2xl': '-1.12px' }}
    >
      {words.map((word, wi) => {
        const letters = word.split('').map((char, li) => {
          const i = letterIndex++;
          let opacity = 0;
          if (progress > 0) {
            if (i < visibleCount) opacity = 1;
            else {
              const diff = i - visibleCount;
              if (diff === 0) opacity = 0.9;
              else if (diff === 1) opacity = 0.7;
              else if (diff === 2) opacity = 0.5;
              else if (diff === 3) opacity = 0.3;
              else if (diff === 4) opacity = 0.2;
            }
          }
          return (
            <MotionSpan
              key={`${wi}-${li}`}
              initial={{ opacity: 1 }}
              animate={{ opacity }}
              transition={{ duration: 0.1 }}
              style={{
                display: 'inline-block',
                whiteSpace: 'pre',
                color: 'white',
              }}
            >
              {char}
            </MotionSpan>
          );
        });

        return (
          <Box
            as="span"
            key={wi}
            display="inline-flex"
            whiteSpace="nowrap"
            mr="0.4rem"
          >
            {letters}
          </Box>
        );
      })}
    </Text>
  );
};

const Section = ({ section, index, scrollY, viewportHeight, isLast }) => {
  const ref = useRef(null);
  const sectionHeight = viewportHeight * 2;
  const [localProgress, setLocalProgress] = useState(0);
  const [localProgressNew, setLocalProgressNew] = useState(0);

  useLayoutEffect(() => {
    if (ref.current) {
      const getValue = document.getElementById(`section-${index + 1}`);
      const rect = ref.current.getBoundingClientRect();
      let rectNext = null;
      if (!isLast && getValue) {
        rectNext = getValue.getBoundingClientRect();
      }
      let lp = (viewportHeight - rect.top) / sectionHeight;
      let lpn = isLast
        ? 0
        : rectNext
        ? (viewportHeight - rectNext.top) / sectionHeight
        : 0;

      lp = Math.min(Math.max(lp, 0), 1);
      lpn = Math.abs(lpn) - 1;
      lpn = Math.min(Math.max(Math.abs(lpn), 0), 1);

      setLocalProgress(lp);
      setLocalProgressNew(lpn);
    }
  }, [scrollY, viewportHeight, index, isLast]); // Re-run on scroll/viewport changes

  const textProgress =
    isLast || localProgress !== 0.5
      ? localProgress > 0.5
        ? (localProgress - 0.5) / 0.5
        : 0
      : Math.abs(localProgressNew) > 0.5
      ? (Math.abs(localProgressNew) - 0.5) / 0.5
      : 0;

  return (
    <Box
      ref={ref}
      id={`section-${index}`}
      height={`200vh`}
      top={0}
      position="sticky"
    >
      <Box position="sticky" top={0} height={`100vh`} zIndex={index + 1}>
        <Box
          height="100vh"
          bgImage={{
            base: `url(${section?.mobileImage?.url})`,
            md: `url(${section?.thumbnail?.url})`,
          }}
          bgSize="cover"
          bgPos="center"
          width="100%"
        />
      </Box>

      {isLast ? (
        <Container
          position="sticky"
          mt={'auto'}
          bottom={0}
          height={'100vh'}
          mx={"auto"}
          textAlign="end"
          style={{ pointerEvents: 'none' }}
          zIndex={index + 2}
          display={'flex'}
          px={'16px'}
          maxWidth={{ base: '100%', md: '1202px' }}
        >
          <Box
            h={'max-content'}
            mt={'auto'}
            mb={{ base: '134px', '2xl': '204px' }}
            width={{base: 'unset', "2xl": '970px'}}
            className="animation-text-container"
          >
            <ScrollAnimatedText
              index={index}
              text={section?.buttonText}
              progress={textProgress}
            />
          </Box>
        </Container>
      ) : (
        <Container
          position="relative"
          mt={'auto'}
          top={'-100vh'}
          height={'100vh'}
          mx={"auto"}
          textAlign="end"
          style={{ pointerEvents: 'none' }}
          zIndex={index + 2}
          display={'flex'}
          px={'16px'}
          maxWidth={{ base: '100%', md: '1202px' }}
        >
          <Box
            h={'max-content'}
            mt={'auto'}
            mb={{ base: '134px', '2xl': '204px' }}
            width={{base: 'unset', "2xl": '970px'}}
            className="animation-text-container"
          >
            <ScrollAnimatedText
              index={index}
              text={section?.buttonText}
              progress={textProgress}
            />
          </Box>
        </Container>
      )}
    </Box>
  );
};

export default function SadhnaSticky({ sectionContent }) {
  const ref = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [vh, setVh] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  useEffect(() => {
    const handle = { ticking: false };

    const onScroll = () => {
      if (!handle.ticking) {
        handle.ticking = true;
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY || window.pageYOffset);
          handle.ticking = false;
        });
      }
    };

    const onResize = () => setVh(window.innerHeight);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    // initialise
    setVh(window.innerHeight);
    setScrollY(window.scrollY || window.pageYOffset);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const cards = [...sectionContent?.[1]?.cards] ?? [];

  return (
    <Box position="relative" background={'#F8F1E7'} ref={ref}>
      <Box
        pt={{ base: '64px', md: "120px", lg: '160px' }}
        pb={{ base: '28px', md: '64px' }}
        px={{ base: '16px', md: '16px' }}
        maxW={'1202px'}
        mx={'auto'}
      >
        <Text
          color="#28231E"
          fontFamily="FedraSansStd-book"
          fontSize={{ base: '24px', md: '32px', lg: '32px' }}
          lineHeight={{ base: '24px', md: '32px', lg: '32px' }}
          letterSpacing={{ base: '4.2px', md: '7.12px', lg: '7.12px' }}
          fontWeight="400"
          textTransform="uppercase"
          className="allSmallCase"
          pb={'8px'}
        >
          {sectionContent?.[0]?.titleText}
        </Text>
        <Text
          color="#2F2F2F"
          fontFamily="FedraSerifDisplayRegular"
          fontSize={{ base: '56px', md: '72px', lg: '72px' }}
          lineHeight={{ base: '56px', md: '72px', lg: '72px' }}
          letterSpacing={{ base: '-1.12px', md: '-1.44px', lg: '-1.44px' }}
          fontWeight="400"
        >
          {sectionContent?.[0]?.titleSubtext}
        </Text>
      </Box>

      <Box>
        {cards.map((section, i) => (
          <Section
            key={i}
            section={section}
            index={i}
            scrollY={scrollY}
            viewportHeight={vh}
            isLast={i === cards.length - 1}
          />
        ))}
      </Box>
      <style jsx global>{`
        .allSmallCase {
          font-variant: all-small-caps;
        }
        .animation-text-container {
          max-width: 100% !important;
        }
        @media (min-width: 1300px) {
          .animation-text-container {
            max-width: 870px !important;
          }
        }
        @media (min-width: 1536px) {
          .animation-text-container {
            max-width: 1202px !important;
          }
        }
      `}</style>
    </Box>
  );
}
